import { useState, useMemo, useRef, useEffect } from "react";
import { Box, Typography, Divider } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useResponsive } from "../../../utils/mobileresponsive";
import { getAdminColumns } from "./Columns";
import SnackbarNotification from "../../../components/ui/SnackBarNotification";
import TicketDetailsDialog from "../sharedcomponents/TicketDialog";
import TableFilters from "../sharedcomponents/TableFilters";
import useFetchTickets from "./hooks/useFetchAllTickets";
import useReplyConversation from "../hooks/useReplyConversation";
import useUpdateTicketStatus from "./hooks/useUpdateTIcketStatus";
import dayjs from "dayjs";

const AdminSupport = () => {
	const { isMobile } = useResponsive();
	const { tickets, loading, refetch } = useFetchTickets();
	const [selectedTicket, setSelectedTicket] = useState(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const [filters, setFilters] = useState({
		search: "",
		category: "",
		status: "",
		date_from: "",
		date_to: "",
	});
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
	const { replyConversation, loading: replyLoading } = useReplyConversation();
	const { updateTicket, loading: updateLoading } = useUpdateTicketStatus();
	const hasHandledQueryParamRef = useRef(false);
	const ticketIdParam = new URLSearchParams(window.location.search).get("ticket_id");

	useEffect(() => {
		const handler = setTimeout(() => {
			setFilters((f) => ({
				...f,
				search: searchInput,
			}));
		}, 250);

		return () => clearTimeout(handler);
	}, [searchInput]);

	useEffect(() => {
		if (!selectedTicket) return;

		const updated = tickets.find((t) => t.id === selectedTicket.id);
		if (updated && updated !== selectedTicket) {
			setSelectedTicket(updated);
		}
	}, [tickets, selectedTicket]);

	useEffect(() => {
		if (hasHandledQueryParamRef.current || loading || !ticketIdParam || !tickets?.length) return;

		const matchedTicket = tickets.find((t) => String(t.id) === String(ticketIdParam));

		if (matchedTicket) {
			handleViewDetails(matchedTicket);
			hasHandledQueryParamRef.current = true;
		}
	}, [tickets, loading, ticketIdParam]);

	const filteredRows = useMemo(() => {
		return tickets.filter((ticket) => {
			const search = filters.search.toLowerCase().trim();

			// ── SEARCH (ticketId + submittedBy)
			const matchesSearch = !search || ticket.ticketId?.toLowerCase().includes(search) || ticket.submittedBy?.toLowerCase().includes(search);

			// ── CATEGORY
			const matchesCategory = !filters.category || ticket.category === filters.category;

			// ── STATUS
			const matchesStatus = !filters.status || ticket.status === filters.status;

			// ── DATE RANGE (dateSubmitted)
			const matchesDateFrom = !filters.date_from || dayjs(ticket.dateSubmitted).isAfter(dayjs(filters.date_from).subtract(1, "day"));

			const matchesDateTo = !filters.date_to || dayjs(ticket.dateSubmitted).isBefore(dayjs(filters.date_to).add(1, "day"));

			return matchesSearch && matchesCategory && matchesStatus && matchesDateFrom && matchesDateTo;
		});
	}, [tickets, filters]);

	const handleViewDetails = (ticket) => {
		setSelectedTicket(ticket);
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
		setSelectedTicket(null);
	};

	const handleStatusChange = async (newStatus, note) => {
		if (!selectedTicket) return;
		try {
			const result = await updateTicket({
				ticket_id: selectedTicket.id,
				status: newStatus,
				note,
			});
			if (!result?.success) {
				setSnackbar({ open: true, message: "Failed to update ticket status. Please try again.", severity: "error" });
				return result;
			}
			setSnackbar({ open: true, message: "Ticket status updated successfully." });
			refetch();
			handleCloseDialog();
			return result;
		} catch (err) {
			console.error(err);
			setSnackbar({ open: true, message: "Unexpected error occurred.", severity: "error" });
		}
	};

	const handleSendReply = async (message) => {
		if (!selectedTicket) return;
		try {
			const result = await replyConversation({
				ticket_id: selectedTicket.id,
				role: "admin",
				message,
			});
			if (!result?.success) {
				setSnackbar({ open: true, message: "Failed to send reply. Please try again.", severity: "error" });
				return result;
			}
			setSnackbar({ open: true, message: "Reply sent succesfully." });
			refetch();
			return result;
		} catch (err) {
			console.error(err);
			setSnackbar({ open: true, message: "Unexpected error occurred.", severity: "error" });
		}
	};

	const columns = getAdminColumns(handleViewDetails);

	return (
		<Box p={4}>
			<Typography variant={isMobile ? "h2" : "h4"} sx={{ fontWeight: "bold !important", color: "#1D54A2", letterSpacing: 4 }}>
				SUPPORT MANAGEMENT
			</Typography>
			<Box my={3}>
				<Divider />
			</Box>

			<TableFilters filters={filters} setFilters={setFilters} searchTerm={searchInput} onSearchChange={setSearchInput} isAgent={false} />

			<DataGrid
				loading={loading}
				rows={filteredRows}
				columns={columns}
				disableColumnMenu
				disableColumnSorting
				disableColumnResize
				hideFooter
				unstable_heightStrategy="content"
				columnHeaderHeight={70}
				getRowHeight={() => "auto"}
				sx={{
					"& .MuiDataGrid-filler": { display: "none !important" },
					"& .MuiDataGrid-columnHeaders": { lineHeight: "normal" },
					"& .MuiDataGrid-columnHeaderTitle": {
						whiteSpace: "normal",
						lineHeight: "1.2",
						overflowWrap: "break-word",
						textAlign: "left",
						fontWeight: 700,
					},
					"& .MuiDataGrid-cell": { py: 1.5 },
				}}
			/>

			<TicketDetailsDialog
				open={dialogOpen}
				ticket={selectedTicket}
				onClose={handleCloseDialog}
				onStatusChange={handleStatusChange}
				onSendReply={handleSendReply}
				isAgent={false}
				replyLoading={replyLoading}
				updateLoading={updateLoading}
			/>

			<SnackbarNotification
				open={snackbar.open}
				message={snackbar.message}
				severity={snackbar.severity}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
			/>
		</Box>
	);
};

export default AdminSupport;
